/**
 * Throttle function
 * @param {Function} cb - Callback function
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Throttled function
 */
function throttle(cb, delay = 1000) {
  let shouldWait = false
  let waitingArgs

  const timeoutFunc = () => {
    if (waitingArgs == null) {
      shouldWait = false
    } else {
      cb(...waitingArgs)
      waitingArgs = null
      setTimeout(timeoutFunc, delay)
    }
  }

  return (...args) => {
    if (shouldWait) {
      waitingArgs = args
      return
    }

    cb(...args)
    shouldWait = true

    setTimeout(timeoutFunc, delay)
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const debug = () => {
  return window.location.hostname === '127.0.0.1' || window.location.hostname.indexOf('myshopify.com') !== -1;
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event?.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

// use as formatMoney(price,'{{amount_with_comma_separator}}')
function formatMoney(cents, format) {
  const moneyFormat = '{{amount}}';
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const fullAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return fullAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

// back to top button
document.querySelectorAll('[data-back-to-top]').forEach(($button) => {
  $button.addEventListener('click', (e) => {
    e.preventDefault();
    window.scroll({top: 0, behavior: 'smooth'});
  });
});


/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt2 = document.createElement('option');
        opt2.value = provinces[i][0];
        opt2.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt2);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class ProductCard extends HTMLElement {
  constructor() {
    super();

    this.productColorSwatchesWrapper = this.querySelector('[data-variant-colors]');
    this.productColorSwatches = Array.from(this.querySelectorAll('[data-variant-color]'));
    this.productColorSwatchesSize = this.productColorSwatches.length;
    this.productColorSwatchesToggle = this.querySelector('[data-variant-color-toggle]');
    this.variantImages = this.querySelectorAll('[data-variant-image]');
    this.isFiltered = false;
    this.hasHiddenSwatches = false

    // Check if the page has been filtered
    if (document.querySelector('collection-filters-form')) {
      this.colorFilterName = document.querySelector('collection-filters-form').querySelector('.filter-options__inner').dataset.colorFilterName;
      this.URLparams = new URLSearchParams(location.search.slice(1)); // remove the '?' from the location.search string
      if (this.URLparams.has(`filter.v.option.${this.colorFilterName}`)) {
        this.isFiltered = true;
      }
    }

    // show respective Variant image on the product color option click
    this.variantImages.forEach(variantImage => variantImage.addEventListener('click', this.showVariantImage.bind(this)));

    if (this.productColorSwatchesWrapper) {
      this.handleColor();

      // on resize, recalculate the swatches display
      this.debouncedOnResize = debounce(() => {
        this.handleColor(this.isFiltered);
      }, 100);
       window.addEventListener('resize', this.debouncedOnResize.bind(this));

      // on tap/click, show all swatches
      (document.body.classList.contains('touch')) ?
      this.productColorSwatchesToggle?.addEventListener('mousedown', this.showAllColors.bind(this)) :
      this.productColorSwatchesToggle?.addEventListener('mouseenter', this.showAllColors.bind(this));

      // hide all swatches and only show the max amount with '+x'
      this.productColorSwatchesWrapper.addEventListener('mouseleave', this.hideAllColors.bind(this));
    }
  }

  showVariantImage(e) {
    let target;
    // get the right target on click or on color filter selection
    if (e.type == 'click') {
      e.preventDefault();
      target = e.currentTarget;
    }
    else {
      target = e;
    }

    let variantURL = target.getAttribute('href');
    let variantImage = target.dataset.variantImage;

    let productCard = target.closest('[data-product-card]');
    let productImage = productCard.querySelector('.product-card__image');
    let productImageURL = productImage.querySelector('.product-card__image--link');
    let productImagePrimary = productImage.querySelector('.product-card__image--primary');

    // remove the active classes and add class to the current variant
    this.productColorSwatches.forEach(color => color.classList.remove('product-card__color--active'));
    target.classList.add('product-card__color--active');

    // replace the href and sources with the new image so the product card links to the correct variant
    productImageURL.setAttribute('href', variantURL);
    productImagePrimary.querySelectorAll('source').forEach(source => source.remove());
    productImagePrimary.querySelector('img').setAttribute('src', variantImage);
    productImagePrimary.querySelector('img').setAttribute('srcset', variantImage);
  }

  /**
   *
   * @param {boolean} isFiltered
   * Parameter is set to true in collection-filters-form.js
   * This function determines how many swatches should be displayed and updates the product card to display the active swatch when collection is filtered.
   */
  handleColor(isFiltered = this.isFiltered) {
    this.isFiltered = isFiltered;

    // first remove all 'visible' classes, in case they were added
    this.productColorSwatches.forEach(swatch => swatch.classList.remove('visible','hidden'));

    // remove padding-left (6) from the colors wrapper if it's a Highlighted product card
    const productCardWidth = this.classList.contains('product-card--large') ? this.productColorSwatchesWrapper.clientWidth - 3 - 6 : this.productColorSwatchesWrapper.clientWidth - 3;

    let hiddenSwatches;
    let hiddenSwatchesLength;
    let hiddenSwatchesLengthFiltered;
    let maxSwatches
    if (window.innerWidth < 1024) {
      maxSwatches = 4
    }
    else {
      maxSwatches = Math.floor(productCardWidth / 26); // swatch width is 26px
    }

    if (this.productColorSwatchesSize > maxSwatches) {
      hiddenSwatches = this.productColorSwatches.filter((swatch, index) => index > maxSwatches - 1);
      hiddenSwatchesLength = hiddenSwatches.length;
      hiddenSwatchesLengthFiltered = hiddenSwatchesLength; // set default first, overwrite it later

      // check for length + 1, because we need one space to display the 'more' button. But if it's not needed, don't display it.
      if (hiddenSwatchesLength > 1) {
        this.hasHiddenSwatches = true;
      }

      // if this is a collection page and there is an active color filter
      if (this.isFiltered) {
        // check if the 'hidden' swatches contain the active color and filter it from the array
        hiddenSwatches = this.productColorSwatches.filter((swatch, index) => index > maxSwatches - 2);
        hiddenSwatches = hiddenSwatches.filter(swatch => !swatch.classList.contains('product-card__color--first'));
        hiddenSwatchesLengthFiltered = hiddenSwatches.length;
      }
    }
    else {
      this.hasHiddenSwatches = false;
    }

    if (this.hasHiddenSwatches) {
      // hide all the swatches that don't fit
      hiddenSwatches.forEach(swatch => swatch.classList.add('hidden'));

      // get the index of the active swatch so we know if it's part of the hidden items
      const activeColorSwatchIndex = this.productColorSwatches.findIndex(swatch => swatch.classList.contains('product-card__color--first'));
      if (activeColorSwatchIndex !== -1) {
        // if the active swatch was hidden, display it again
        this.productColorSwatches[activeColorSwatchIndex].classList.remove('hidden');
      }
      else {
        // active swatch should be hidden but is moved to the front, so deduct another item from the total and add hidden class
        if (hiddenSwatchesLengthFiltered < hiddenSwatchesLength) {
          const remainingSwatchesIndex = this.productColorSwatchesSize - maxSwatches + 2; // + 2 to include the active swatch and because we need to display an item instead of the active swatch
          const hiddenSwatchesWithActiveItem = this.productColorSwatches.slice(-remainingSwatchesIndex);
          // add the hidden class to the item replacing the active item
          hiddenSwatchesWithActiveItem.forEach(swatch => (!swatch.classList.contains('product-card__color--first') && !swatch.classList.contains('hidden')) ? swatch.classList.add('hidden') : '')
        }
      }

      // display the trigger button
      this.productColorSwatchesToggle.classList.remove('hidden');
      this.productColorSwatchesToggle.innerText = `+${hiddenSwatches.length}`;
    }
    else {
      // show all swatches and hide the trigger button
      this.productColorSwatches.forEach(swatch => swatch.classList.remove('hidden'));
      this.productColorSwatchesToggle.classList.add('hidden');
    }
  }

  hideAllColors() {
    if (this.hasHiddenSwatches) {
      this.productColorSwatchesToggle.classList.remove('hidden');
      this.productColorSwatches.forEach(swatch => swatch.classList.remove('visible'));
    }
  }

  showAllColors() {
    if (this.hasHiddenSwatches) {
      this.productColorSwatchesToggle.classList.add('hidden');
      this.productColorSwatches.forEach(swatch => (swatch.classList.contains('hidden') ? swatch.classList.add('visible') : ''))
    }
  }
}

if(!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
